import { useCallback, useEffect, useState } from "react";
import { TFormHealth, TPaymentFrequency } from "..";
import { MGChip } from "../../../designSystem/components/MGChip";
import { MGInputRadioButton } from "../../../designSystem/components/MGInputRadioButton";
import { MGLabel } from "../../../designSystem/components/MGLabel";
import { MGMicrotext } from "../../../designSystem/components/MGMicrotext";
import { MGSubtitle } from "../../../designSystem/components/MGSubtitle";
import styles from "../Health.module.css";
import { paymentFrequencyOptions } from "./types";
import { MGCard } from "../../../designSystem/components/MGCard";
import useMyMontepioContext from "../../../components/Context/useMyMontepioContext";
import { MGText } from "../../../designSystem/components/MGText";
import { MGColors } from "../../../designSystem/colors";
import { MGButton } from "../../../designSystem/components/MGButton";
import { MGInput } from "../../../designSystem/components/MGInput";
import { MGTag } from "../../../designSystem/components/MGTag";
import { MGNote } from "../../../designSystem/components/MGNote";
import stethoscopeIcon from "../../../images/stethoscope-squre.svg";
import quotasStyles from "./QuotaInsurancePlanStep.module.css";
import { Plus } from "lucide-react";
import healthIcon from "../../../images/medical-cross-shield.svg";
import { useSimulateMutation } from "../api";
import { MGLoader } from "../../../designSystem/components/MGLoader";
import { formatCurrency } from "../../../utils/formatCurrency";
import { MGGenericError } from "../../../designSystem/components/MGGenericError";
import { BackgroundForm } from "../../../components/BackgroundForm";

type TQuotaInsurancePlanStepProps = {
  formHealth: TFormHealth;
  onContinue: (formHealth: TFormHealth) => void;
  onBack: VoidFunction;
};

const quota: Record<TPaymentFrequency, number> = {
  MONTHLY: 12,
  QUARTERLY: 4,
  SEMIANNUAL: 2,
  ANNUAL: 1,
};

export const QuotaInsurancePlanStep = ({
  formHealth,
  onContinue,
  onBack,
}: TQuotaInsurancePlanStepProps) => {
  const { isMobileAuth } = useMyMontepioContext();

  const [selectedFrequency, setSelectedFrequency] = useState<TPaymentFrequency>(
    formHealth.protection?.paymentFrequency ?? "ANNUAL",
  );
  const [hasFrequency, setHasFrequency] = useState(
    !!formHealth.protection?.paymentFrequency &&
      formHealth.protection.paymentFrequency !== "ANNUAL",
  );

  const { data, mutate, isPending, isError } = useSimulateMutation(selectedFrequency);

  const simulate = useCallback(() => {
    const { dateOfBirth, gender, moduleType, paymentFrequency } = formHealth;

    if (!dateOfBirth || !gender || !moduleType || !paymentFrequency) {
      return;
    }

    mutate({
      associate: {
        dateOfBirth,
        gender,
      },
      healthInsurancePlan: {
        supplementaryHealthPlan: true,
        moduleType,
        paymentFrequency,
        supplementaryHealthPaymentFrequency: selectedFrequency,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mutate,
    selectedFrequency,
    formHealth.dateOfBirth,
    formHealth.gender,
    formHealth.moduleType,
    formHealth.paymentFrequency,
  ]);

  useEffect(() => {
    simulate();
  }, [simulate]);

  const onDone = useCallback(() => {
    onContinue({
      ...formHealth,
      protection: { paymentFrequency: selectedFrequency },
      simulateResponse: data,
    });
  }, [data, formHealth, onContinue, selectedFrequency]);

  if (isPending) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <MGGenericError
          actionsAtBottom={isMobileAuth}
          title="Mensagem de Erro !"
          description="Explicação adicional, próximos passos e outras opções de contexto"
          primaryActionLabel="Tentar novamente"
          secondaryActionLabel="Voltar"
          onPrimaryAction={simulate}
          onSecondaryAction={onBack}
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flex: 1, flexDirection: "column" }}>
      <MGLabel style={{ marginTop: 8, marginBottom: 32 }} labelStyleDefinition={"80-03"}>
        {"Texto a definir"}
      </MGLabel>

      <BackgroundForm style={{ marginBottom: isMobileAuth ? 32 : 0 }}>
        <MGInput labelText="Metodo de Pagamento" readOnly value="Débito Direto" />

        <MGSubtitle style={{ marginTop: 32, marginBottom: 16 }} subtitleStyleDefinition="100-06">
          {"Opção de Pagamento"}
        </MGSubtitle>

        <MGLabel style={{ marginBottom: 16 }} labelStyleDefinition="80-03">
          {"... Possibilidade de fracionamento Mensal, Trimestral, Semestral ..."}
        </MGLabel>

        <MGMicrotext style={{ marginBottom: 16 }} microtextStyleDefinition="microcopy-100-05">
          {"Pretende fracionar a quota anual?"}
        </MGMicrotext>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <MGChip
            label="Não"
            selected={!hasFrequency}
            onClick={() => {
              setHasFrequency(false);
              setSelectedFrequency("ANNUAL");
            }}
          />
          <MGChip
            label="Sim"
            selected={hasFrequency}
            onClick={() => {
              setHasFrequency(true);
              setSelectedFrequency("MONTHLY");
            }}
          />
        </div>

        {hasFrequency && (
          <MGNote
            icon="info"
            description="Texto taxa de fracionamento "
            backgroundColor="transparent"
            style={{ marginBottom: 24 }}
          />
        )}

        {hasFrequency && (
          <MGMicrotext style={{ marginBottom: 24 }} microtextStyleDefinition="microcopy-100-05">
            {"Como pretende pagar ?"}
          </MGMicrotext>
        )}

        {hasFrequency && (
          <div className={styles.radioList}>
            {paymentFrequencyOptions
              .filter((frequency) => frequency.value !== "ANNUAL")
              .map((option) => (
                <div key={option.value} className={styles.radioOption}>
                  <MGInputRadioButton
                    id={`gender-${option.value}`}
                    value={option.value}
                    checked={selectedFrequency === option.value}
                    onChange={() => setSelectedFrequency(option.value)}
                  />
                  <MGMicrotext
                    key={option.value}
                    tagName="label"
                    htmlFor={`gender-${option.value}`}
                    microtextStyleDefinition={
                      selectedFrequency === option.value ? "microcopy-100-05" : "microcopy-100-03"
                    }>
                    {option.name}
                  </MGMicrotext>
                </div>
              ))}
          </div>
        )}

        <MGCard backgroundColor="white" style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 16,
              padding: "0 8px",
            }}>
            <div
              style={{
                display: "flex",
                width: 40,
                height: 40,
                backgroundColor: MGColors["02-70"].hex,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}>
              <img src={healthIcon} width={24} height={24} alt="" />
            </div>

            <MGMicrotext microtextStyleDefinition="microcopy-100-05" color="text-primary-02">
              {"Plano Assegura a Saúde"}
            </MGMicrotext>
          </div>

          <div
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: MGColors["neutral-200"].hex,
              padding: 16,
            }}>
            {hasFrequency && (
              <MGTag
                style={{ marginBottom: 16 }}
                label="Pagamento Fracionado"
                appearance={"blue"}
                size="xxs"
              />
            )}

            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
              {hasFrequency && (
                <div
                  style={{
                    display: "flex",
                    backgroundColor: MGColors["background-primary-02"].hex,
                    borderRadius: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 56,
                    width: 56,
                  }}>
                  <MGLabel labelStyleDefinition="400-06">{`${quota[selectedFrequency]} x`}</MGLabel>
                </div>
              )}

              <div>
                <MGText textStyleDefinition="body-100-05" color="text-primary-03">
                  {selectedFrequency === "ANNUAL" ? "Valor da quota anual" : "Valor da quota"}
                </MGText>

                <MGLabel
                  style={{ display: "inline-block", verticalAlign: "middle" }}
                  labelStyleDefinition="400-06">
                  {data ? formatCurrency(data.protection.installmentAmount ?? 0) : ""}
                </MGLabel>
              </div>
            </div>
          </div>
        </MGCard>

        <div className={quotasStyles.planConnector} role="separator" aria-label="Mais">
          <span className={quotasStyles.planConnectorIcon} aria-hidden="true">
            <Plus size={16} strokeWidth={3} />
          </span>
        </div>

        <MGCard backgroundColor="white" style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 16,
              padding: "0 8px",
            }}>
            <div
              style={{
                display: "flex",
                width: 40,
                height: 40,
                backgroundColor: MGColors["02-70"].hex,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}>
              <img src={stethoscopeIcon} width={24} height={24} alt="" />
            </div>

            <MGMicrotext microtextStyleDefinition="microcopy-100-05" color="text-primary-02">
              {"Modalidade de Saúde"}
            </MGMicrotext>
          </div>

          <div
            style={{
              borderRadius: 8,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: MGColors["neutral-200"].hex,
              padding: 16,
            }}>
            {formHealth.paymentFrequency !== "ANNUAL" && (
              <MGTag
                style={{ marginBottom: 16 }}
                label="Pagamento Fracionado"
                appearance={"blue"}
                size="xxs"
              />
            )}

            <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
              {formHealth.paymentFrequency !== "ANNUAL" && (
                <div
                  style={{
                    display: "flex",
                    backgroundColor: MGColors["background-primary-02"].hex,
                    borderRadius: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 56,
                    width: 56,
                  }}>
                  <MGLabel labelStyleDefinition="400-06">{`${quota[formHealth.paymentFrequency!]} x`}</MGLabel>
                </div>
              )}

              <div>
                <MGText textStyleDefinition="body-100-05" color="text-primary-03">
                  {formHealth.paymentFrequency! === "ANNUAL"
                    ? "Valor da quota anual"
                    : "Valor da quota"}
                </MGText>

                <MGLabel labelStyleDefinition="400-06">
                  {data ? formatCurrency(data.finalAmount) : ""}{" "}
                  <MGLabel tagName="span" labelStyleDefinition="300-03">
                    {"(IVA Inc.)"}
                  </MGLabel>
                </MGLabel>
              </div>
            </div>
          </div>
        </MGCard>
      </BackgroundForm>

      <MGButton
        fullWidth={isMobileAuth}
        className={`${styles.buttonFooter} ${isMobileAuth ? styles.buttonFooterFixed : ""}`}
        onClick={onDone}>
        {"Continuar"}
      </MGButton>
    </div>
  );
};
